import { RefreshCw, ShieldAlert, Users, type LucideIcon } from "lucide-react";
import { DashboardCardShell } from "@/components/dashboard/DashboardCardShell";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { DashboardStateSkeleton } from "@/components/dashboard/DashboardState";
import type { CurrentUser } from "@/features/auth/currentUserTypes";
import { useCurrentUser } from "@/features/auth/useCurrentUser";
import { DashboardPage } from "@/pages/dashboard/DashboardPage";

function isSupportedDashboardRole(role: unknown): role is CurrentUser["role"] {
  return (
    role === "super" ||
    role === "admin_helper" ||
    role === "client" ||
    role === "client_store" ||
    role === "supplier" ||
    role === "checker" ||
    role === "overviewer"
  );
}

interface HomeAccessStateProps {
  testId: string;
  role?: "alert" | "status";
  icon: LucideIcon;
  title: string;
  description: string;
  tone?: "error" | "warning";
  actionLabel?: string;
  onAction?: () => void;
}

function HomeAccessState({
  testId,
  role = "status",
  icon: Icon,
  title,
  description,
  tone = "warning",
  actionLabel,
  onAction,
}: HomeAccessStateProps) {
  const accentColor =
    tone === "error"
      ? "var(--dashboard-semantic-negative)"
      : "var(--dashboard-semantic-warning)";

  return (
    <DashboardShell>
      <DashboardCardShell
        testId={testId}
        accentColor={accentColor}
        style={{
          alignSelf: "center",
          margin: "clamp(1.5rem, 8vh, 5rem) 0",
          maxWidth: "36rem",
          padding: "clamp(1rem, 3vw, 1.35rem)",
          width: "100%",
        }}
      >
        <div
          role={role}
          style={{
            alignItems: "start",
            display: "grid",
            gap: "0.8rem",
            gridTemplateColumns: "auto minmax(0, 1fr)",
            minWidth: 0,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              alignItems: "center",
              background: `color-mix(in srgb, ${accentColor} 12%, transparent)`,
              borderRadius: "0.65rem",
              color: accentColor,
              display: "inline-flex",
              height: "2.5rem",
              justifyContent: "center",
              width: "2.5rem",
            }}
          >
            <Icon size={20} strokeWidth={2.15} />
          </span>
          <div style={{ minWidth: 0 }}>
            <h1
              style={{
                color: "var(--login-text)",
                fontFamily: "'Barlow Condensed', sans-serif",
                fontSize: "clamp(1.25rem, 3vw, 1.5rem)",
                fontWeight: 800,
                letterSpacing: "0.015em",
                lineHeight: 1.1,
                margin: 0,
              }}
            >
              {title}
            </h1>
            <p
              style={{
                color: "var(--login-muted)",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "clamp(0.88rem, 2vw, 0.96rem)",
                lineHeight: 1.5,
                margin: "0.35rem 0 0",
              }}
            >
              {description}
            </p>
            {actionLabel && onAction ? (
              <button
                type="button"
                onClick={onAction}
                style={{
                  background: "var(--login-accent)",
                  border: 0,
                  borderRadius: "0.55rem",
                  color: "var(--dashboard-control-active-text)",
                  cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "0.88rem",
                  fontWeight: 750,
                  marginTop: "0.8rem",
                  minHeight: "44px",
                  padding: "0 1rem",
                }}
              >
                {actionLabel}
              </button>
            ) : null}
          </div>
        </div>
      </DashboardCardShell>
    </DashboardShell>
  );
}

function HomeLoadingState() {
  return (
    <DashboardShell>
      <div
        data-testid="home-dashboard-loading"
        aria-busy="true"
        aria-label="Carregando acesso ao painel"
        style={{ minHeight: "24.25rem", minWidth: 0 }}
      >
        <DashboardStateSkeleton />
      </div>
    </DashboardShell>
  );
}

export function HomePage() {
  const currentUser = useCurrentUser();

  if (currentUser.isLoading && !currentUser.data) {
    return <HomeLoadingState />;
  }
  if (currentUser.error && !currentUser.data) {
    return (
      <HomeAccessState
        testId="home-dashboard-error"
        role="alert"
        icon={RefreshCw}
        title="Não foi possível carregar seu acesso"
        description="Tente novamente para abrir o painel."
        tone="error"
        actionLabel="Tentar novamente"
        onAction={() => void currentUser.refetch()}
      />
    );
  }
  if (!currentUser.data) {
    return (
      <HomeAccessState
        testId="home-dashboard-unavailable"
        role="alert"
        icon={Users}
        title="Acesso ao painel indisponível"
        description="Não foi possível confirmar seu perfil. Entre novamente e tente de novo."
      />
    );
  }
  if (!isSupportedDashboardRole(currentUser.data.role)) {
    return (
      <HomeAccessState
        testId="home-dashboard-unsupported"
        icon={ShieldAlert}
        title="Perfil sem painel"
        description="Este perfil não possui uma visão inicial configurada."
      />
    );
  }

  return <DashboardPage user={currentUser.data} />;
}
